import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface LegacyLoginFormProps {
  onSubmit: (server: string, username: string, password: string) => void;
  disabled?: boolean;
}

const isProduction = import.meta.env.VITE_ENVIRONMENT === 'prod';

export function LegacyLoginForm({ onSubmit, disabled }: LegacyLoginFormProps) {
  const [server, setServer] = useState<string>('prod');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!server || !username.trim() || !password) return;
    onSubmit(server, username.trim(), password);
    setPassword('');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Import Legacy Inventory</CardTitle>
        <CardDescription>
          Enter your legacy DFE credentials to import your inventory into Fias. Each legacy account
          can only be imported once.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="server">Server</Label>
            <Select value={server} onValueChange={setServer} disabled={disabled}>
              <SelectTrigger id="server">
                <SelectValue placeholder="Select server" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="prod">Production</SelectItem>
                {!isProduction && <SelectItem value="staging">Staging</SelectItem>}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Your legacy DFE username"
              disabled={disabled}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your legacy DFE password"
              disabled={disabled}
            />
          </div>
          <Button
            type="submit"
            disabled={disabled || !server || !username.trim() || !password}
            className="w-full"
          >
            {disabled ? 'Importing...' : 'Import Inventory'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
